import { Link } from "react-router-dom";
import { CompassLogo } from "../components/CompassLogo";
import { ArrowLeft } from "lucide-react";
import { newUi } from "../lib/featureFlags";
import PrivacyNoticeNew from "./PrivacyNoticeNew";
import {
  PRIVACY_NOTICE_TITLE,
  PRIVACY_NOTICE_INTRO,
  DATA_CATEGORIES,
  WHO_CAN_SEE,
  CHILDREN_AND_GUARDIANS,
  WHAT_LEAVES,
  DELETION_AND_CONTACT_SHIPPED,
  COMING_LATER_SHIPPED,
} from "../lib/privacyNotice";

/**
 * `/privacy` — the INTERIM privacy notice (issue #449, partial). See
 * `lib/privacyNotice.js` for why this exists, what it deliberately does
 * not promise, and where every fact comes from.
 *
 * PUBLIC AND UNAUTHENTICATED, on purpose (App.js mounts it outside
 * `ProtectedRoute`, next to `/register` and `/login`): a parent deciding
 * whether to let their child sign up should be able to read this before
 * an account exists, not after.
 *
 * All copy is read from `lib/privacyNotice.js` rather than written inline
 * here, the same discipline `lib/usageDisclosure.js` established — one
 * module a test can pin, one place that can drift from the code.
 */
export default function PrivacyNotice() {
  // Issue #974, D186.12 (orchestrator ruling, for Andreas's veto) —
  // `privacy` is its own flag, with NO chain (unlike `dev-chrome`'s own
  // `&& isPlayer`, DevelopmentPlan.jsx:299, this needs no second term).
  // The check is still bound to a variable first, not written directly
  // inside the `if`'s own parens — the same shape DevelopmentPlan.jsx's
  // own `devChromeOn` uses, and for the same reason: so
  // flagSafeLinks.test.js's route-gating derivation does not mistake this
  // IN-PAGE gate for a route App.js itself branches on. `/privacy`
  // (App.js:107) renders `<PrivacyNotice/>` unconditionally, flag on or
  // off — the flag decides what THIS component returns, never which
  // component the route mounts.
  const privacyOn = newUi("privacy");
  if (privacyOn) return <PrivacyNoticeNew />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-notice-page">
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-lg px-3 py-2 inline-flex mb-6">
          <CompassLogo size={44} />
        </div>

        <p
          className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 bg-amber-100 rounded-full px-3 py-1 mb-4"
          data-testid="privacy-notice-interim-badge"
        >
          Interim notice
        </p>

        <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-900 min-w-0">
          {PRIVACY_NOTICE_TITLE}
        </h1>

        <div className="mt-4 space-y-3">
          {PRIVACY_NOTICE_INTRO.map((paragraph) => (
            <p key={paragraph} className="text-sm sm:text-base text-slate-700 min-w-0">
              {paragraph}
            </p>
          ))}
        </div>

        <section className="mt-8" data-testid="privacy-notice-categories">
          <h2 className="font-display font-black text-lg text-slate-900">What we hold</h2>
          <div className="mt-3 space-y-4">
            {DATA_CATEGORIES.map((category) => (
              <div key={category.id} id={category.id} className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">{category.label}</h3>
                <p className="text-sm text-slate-600 mt-0.5 min-w-0">{category.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8" data-testid="privacy-notice-who-can-see">
          <h2 className="font-display font-black text-lg text-slate-900">Who can see what</h2>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            {WHO_CAN_SEE.map((paragraph) => (
              <li key={paragraph} className="text-sm text-slate-600 min-w-0">
                {paragraph}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8" data-testid="privacy-notice-children">
          <h2 className="font-display font-black text-lg text-slate-900">Children and guardians</h2>
          <div className="mt-3 space-y-2">
            {CHILDREN_AND_GUARDIANS.map((paragraph) => (
              <p key={paragraph} className="text-sm text-slate-600 min-w-0">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        <section className="mt-8" data-testid="privacy-notice-what-leaves">
          <h2 className="font-display font-black text-lg text-slate-900">What leaves our systems</h2>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            {WHAT_LEAVES.map((paragraph) => (
              <li key={paragraph} className="text-sm text-slate-600 min-w-0">
                {paragraph}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8" data-testid="privacy-notice-deletion">
          <h2 className="font-display font-black text-lg text-slate-900">
            Deletion, and how to ask us about your data
          </h2>
          <div className="mt-3 space-y-2">
            {DELETION_AND_CONTACT_SHIPPED.map((paragraph) => (
              <p key={paragraph} className="text-sm text-slate-600 min-w-0">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        <div className="mt-8 p-4 rounded-xl bg-slate-100 border border-slate-200" data-testid="privacy-notice-coming-later">
          <p className="text-sm text-slate-600 min-w-0">{COMING_LATER_SHIPPED}</p>
        </div>

        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="privacy-notice-back"
        >
          <ArrowLeft size={14} /> Back to CricketCompass
        </Link>
      </div>
    </div>
  );
}
